import { parse } from 'tldts';

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
export function validateExternalUrl(
  urlString: string,
  opts?: { maxLength?: number },
): { ok: true } | { ok: false; error: string } {
  const maxLength = opts?.maxLength ?? 200;
  if (!urlString || typeof urlString !== 'string') {
    return { ok: false, error: 'URL must be a non-empty string' };
  }
  const trimmed = urlString.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: 'URL must not be empty' };
  }
  if (trimmed.length > maxLength) {
    return { ok: false, error: `URL is too long (max ${maxLength} chars)` };
  }
  if (!trimmed.startsWith('https://')) {
    return { ok: false, error: 'URL must start with https://' };
  }
  let u: URL;
  try {
    u = new URL(trimmed);
  } catch (_e) {
    return { ok: false, error: 'Invalid URL format' };
  }
  if (u.protocol !== 'https:') {
    return { ok: false, error: 'Only https URLs are allowed' };
  }
  if (u.username || u.password) {
    return { ok: false, error: 'Credentials in URL are not allowed' };
  }
  if (u.port && u.port !== '80' && u.port !== '443') {
    return { ok: false, error: 'Only standard web ports 80/443 are allowed' };
  }
  const host = u.hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
    return { ok: false, error: 'Localhost is not allowed' };
  }
  const privateIpPatterns = [
    /^10\./,
    /^192\.168\./,
    /^172\.(1[6-9]|2\d|3[0-1])\./,
  ];
  if (privateIpPatterns.some(re => re.test(host))) {
    return { ok: false, error: 'Private network addresses are not allowed' };
  }
  const ipv4 = /^(?:\d{1,3}\.){3}\d{1,3}$/;
  const ipv6 = /^(?:\[?[a-f0-9:]+\]?)/i;
  if (ipv4.test(host) || (host.includes(':') && ipv6.test(host))) {
    return { ok: false, error: 'IP addresses are not allowed; use a domain' };
  }
  if (!host.includes('.')) {
    return {
      ok: false,
      error: 'Domain must include a dot (e.g., example.com)',
    };
  }
  const labelRe = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;
  const labels = host.split('.').filter(Boolean);
  if (labels.some(l => !labelRe.test(l))) {
    return { ok: false, error: 'Domain contains invalid label characters' };
  }
  // Validate TLD using tldts library
  // parse() returns publicSuffix as null if the TLD is not in the public suffix list
  const parsed = parse(host);
  if (!parsed.publicSuffix || !parsed.isIcann) {
    return { ok: false, error: 'Invalid or unsupported top-level domain' };
  }
  if (u.search && u.search.length > 512) {
    return { ok: false, error: 'Query string is too long' };
  }
  return { ok: true };
}
